import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
const FocusableContext = /*#__PURE__*/React.createContext(true);

const useFocusable = () => {
  // Allow code to work in environments that havent upgraded react
  // This is neccessary because this code will be used in pagelets (via buttons)
  if (React.useContext) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return React.useContext(FocusableContext);
  }

  return true;
};
/**
 * Render a `FocusBoundary` that allows control over child focusable elements.
 *
 * Focusable children will allow focus if all parent `FocusBoundary`'s are enabled
 */


const FocusBoundary = _ref => {
  let {
    enabled,
    detached,
    children
  } = _ref;
  const parentIsFocusable = useFocusable();
  const isEnabled = enabled && (detached || parentIsFocusable);
  return /*#__PURE__*/React.createElement(FocusableContext.Provider, {
    value: isEnabled
  }, children);
};

process.env.NODE_ENV !== "production" ? FocusBoundary.propTypes = {
  /**
   * Children.
   */
  children: PropTypes.node.isRequired,

  /**
   * Ignore any parent `FocusBoundary`. This is useful when the component tree does
   * not represent the HTML tree (For example, when using React portals).
   */
  detached: PropTypes.bool,

  /**
   * Whether or not its children should be focusable
   */
  enabled: PropTypes.bool
} : void 0;
/**
 * Render an element that can receive focus.
 *
 * If this element has a `FocusBoundary` as a parent, the `FocusBoundary` will have
 * partial control over the `tabIndex` of the underlying `Focusable` element. If the `FocusBoundary`
 * is `enabled`, it will not affect the rendering of the underlying element; otherwise,
 * `tabIndex="-1"` will be passed to the underlying element.
 */

const Focusable = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    as: ComponentToRender,
    ...props
  } = _ref2;
  const focusProps = useFocusable() ? {} : {
    tabIndex: '-1'
  };
  return /*#__PURE__*/React.createElement(ComponentToRender, _extends({
    ref: ref
  }, props, focusProps));
});
Focusable.displayName = 'Focusable';
process.env.NODE_ENV !== "production" ? Focusable.propTypes = {
  /**
   * Underlying element type to render
   */
  as: PropTypes.elementType
} : void 0;
export { FocusBoundary, Focusable };